import { EventTimeline, EventType } from 'matrix-js-sdk/src';
import { MatrixEvent, RoomStateEvent } from 'matrix-js-sdk/src/matrix';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import { fetchMediaWithAuth, uploadAvatarFile } from 'modules/Authentication/utils';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';

interface UseRoomAvatarOptions {
  roomId?: string | null;
  size?: number;
}

const useRoomAvatar = ({ roomId, size = 248 }: UseRoomAvatarOptions) => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const { workspaceAvatarUrls, setWorkspaceAvatarUrl, clearWorkspaceAvatarUrl, workspacePermissions } =
    useWorkspacesContext();
  const [loading, setLoading] = useState(false);
  const avatarUrl = roomId ? workspaceAvatarUrls[roomId] : undefined;

  useEffect(() => {
    const loadAvatar = async () => {
      if (!roomId) return;
      const room = matrixClient.getRoom(roomId);
      if (!room) return;

      const cachedUrl = workspaceAvatarUrls[roomId];
      if (cachedUrl) return;

      const state = room.getLiveTimeline().getState(EventTimeline.FORWARDS);
      const avatarEvent = state?.getStateEvents(EventType.RoomAvatar, '');
      const mxcUrl = avatarEvent?.getContent()?.url as string | undefined;
      if (!mxcUrl) return;

      const httpUrl = matrixClient.mxcUrlToHttp(mxcUrl, size, size, 'scale', undefined, undefined, true);
      if (!httpUrl) return;

      const accessToken = matrixClient.getAccessToken();
      if (!accessToken) return setWorkspaceAvatarUrl(roomId, httpUrl);

      try {
        const response = await fetchMediaWithAuth(httpUrl, accessToken);
        const responseData = await response.arrayBuffer();
        const blob = new Blob([responseData], { type: 'image/jpeg' });
        setWorkspaceAvatarUrl(roomId, URL.createObjectURL(blob));
      } catch {
        setWorkspaceAvatarUrl(roomId, httpUrl);
      }
    };

    loadAvatar();
  }, [matrixClient, roomId, size, workspaceAvatarUrls, setWorkspaceAvatarUrl]);

  useEffect(() => {
    if (!matrixClient || !roomId) return;

    const handleStateEventUpdate = (event: MatrixEvent) => {
      if (event.getType() !== EventType.RoomAvatar || event.getRoomId() !== roomId) return;
      clearWorkspaceAvatarUrl(roomId);
    };

    matrixClient.on(RoomStateEvent.Events, handleStateEventUpdate);

    return () => {
      matrixClient.off(RoomStateEvent.Events, handleStateEventUpdate);
    };
  }, [clearWorkspaceAvatarUrl, matrixClient, roomId, setWorkspaceAvatarUrl]);

  const changeAvatar = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (!workspacePermissions.workspaceLogo) return pushNoPermissionToast();

    try {
      setLoading(true);
      const [file] = target.files || [];
      if (!file || !roomId) return;

      const uploadResult = await uploadAvatarFile(matrixClient, file);
      if (!uploadResult?.contentUri) return;

      await matrixClient.sendStateEvent(roomId, EventType.RoomAvatar, { url: uploadResult.contentUri }, '');
      setWorkspaceAvatarUrl(roomId, uploadResult.localUrl);
    } catch (error) {
      logger.error('Error uploading workspace avatar:', { error, roomId });
      pushToast({ title: t('something_went_wrong_try_again_later'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const clearAvatar = async () => {
    if (!workspacePermissions.workspaceLogo) return pushNoPermissionToast();
    if (!roomId) return;

    try {
      setLoading(true);

      await matrixClient.sendStateEvent(roomId, EventType.RoomAvatar, {}, '');
    } catch (error) {
      logger.error('Error clearing workspace avatar:', { error, roomId });
      pushToast({ title: t('something_went_wrong_try_again_later'), variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return { avatarUrl, changeAvatar, clearAvatar, loading };
};

export default useRoomAvatar;
